import { useRef } from 'react';

import { IPlaylistContentItem } from 'contexts/playlists/types';
import Icon from 'components/icons';
import Tooltip from 'components/TooltipV2';
import { colors } from 'constants/styles/colors';
import { screenSm } from 'constants/styles/breakpoints';
import styled from 'styled-components';
import { CONTENT_STATUS, CONTENT_TYPES, LAB_TYPES } from 'constants/content/content';
import { FLAGLESS_LAB_TOOLTIP } from 'constants/playlists';
import { pluralize } from 'utils/string';
import { getEligibleCollectionLabs } from 'utils/collectionLabs';

export const ContentProgressBarContainer = styled.div`
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #57575c;
  gap: 10px;
  margin-left: 20px;
  ${screenSm} {
    margin: 0px;
  }
`;

const BarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 160px;
  height: 4px;
  position: relative;
  background: #f2f2f7;
  border-radius: 30px;
`;

const StatusLabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100px;
`;

const Bar = styled.div<{ progress?: number }>`
  width: ${({ progress = 0 }) => progress}%;
  height: 4px;
  position: absolute;
  top: 0;
  left: 0;
  background: #a793f6;
  border-radius: 50px;
`;

const Step = styled.div<{ active: boolean; position?: number }>`
  position: absolute;
  left: ${({ position = 0 }) => position}%;
  transform: translateX(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ active }) => (active ? '#a793f6' : '#f2f2f7')};
`;

const userStatusLabelMap = {
  unstarted: 'Not started',
  started: 'Started',
  finished: 'Completed',
};

const getTooltipText = (contentType, statusKey, progress = null) => {
  let contentTypeLabel;
  if (contentType === CONTENT_TYPES.LEARNING_PATH) {
    contentTypeLabel = 'Learning path';
  } else
    contentTypeLabel = contentType
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

  switch (statusKey) {
    case CONTENT_STATUS.UNSTARTED:
      return `${contentTypeLabel} not started`;
    case CONTENT_STATUS.STARTED:
      return !progress
        ? `${contentTypeLabel} started`
        : `${progress}% of the Skill Check total score achieved`;
    case CONTENT_STATUS.FINISHED:
      return `${contentTypeLabel} complete`;
    case CONTENT_STATUS.NON_TRACKABLE:
      return `This ${contentTypeLabel} only tracks ‘Not started’ and ‘Started’ statuses and doesn’t count towards playlist completion`;
  }
};

const getLabelStatusText = (statusKey, isTrackable, progress = null) => {
  if (!isTrackable && statusKey === CONTENT_STATUS.FINISHED) {
    return userStatusLabelMap.started;
  }
  if (statusKey === CONTENT_STATUS.STARTED && progress) {
    return `Score ${progress}%`;
  }
  return userStatusLabelMap[statusKey];
};

// RFC §3.2 display ratio: flagged labs count once finished, flagless labs once
// started, over the collection's *eligible* (published) labs only -- a draft
// lab is not content the learner can complete, so counting it in the
// denominator would strand the ratio below 100% forever.
export const getCollectionLabProgress = (
  labs: { has_flags?: boolean; user_status?: string; status?: string }[] = []
): { complete: number; total: number; percentage: number } => {
  const eligibleLabs = getEligibleCollectionLabs(labs);
  const total = eligibleLabs.length;
  const complete = eligibleLabs.filter(({ has_flags, user_status }) =>
    has_flags
      ? user_status === CONTENT_STATUS.FINISHED
      : user_status === CONTENT_STATUS.STARTED || user_status === CONTENT_STATUS.FINISHED
  ).length;

  return { complete, total, percentage: total ? Math.round((complete / total) * 100) : 0 };
};

interface IContentProgressBar {
  item: IPlaylistContentItem;
}

const ContentProgressBar: React.FC<IContentProgressBar> = ({ item }) => {
  const progressBarRef = useRef<HTMLDivElement>(null);

  let isTrackable;

  let isStarted;
  let isUnstarted;
  let isCompleted;
  let progress;

  let statusText;
  let userStatusKey;
  let infoTooltipText;

  switch (item.content_type) {
    case CONTENT_TYPES.VIDEO:
      userStatusKey = item.user_status as string;

      isTrackable = item.is_trackable;

      isStarted = userStatusKey === CONTENT_STATUS.STARTED;
      isUnstarted = userStatusKey === CONTENT_STATUS.UNSTARTED;
      isCompleted = userStatusKey === CONTENT_STATUS.FINISHED;

      statusText = getLabelStatusText(userStatusKey, isTrackable);

      infoTooltipText = getTooltipText(item.content_type, userStatusKey);

      progress = item.watched_percentage > 100 ? 100 : item.watched_percentage;

      break;
    case CONTENT_TYPES.LAB: {
      userStatusKey = item.user_status as string;

      // A flagless lab never reaches `finished` via flag submission, so started
      // is complete. `has_flags` is only present on a collection's child rows.
      if (item.has_flags === false && userStatusKey === CONTENT_STATUS.STARTED) {
        userStatusKey = CONTENT_STATUS.FINISHED;
      }

      isUnstarted = userStatusKey === CONTENT_STATUS.UNSTARTED;
      isStarted = userStatusKey === CONTENT_STATUS.STARTED;
      isCompleted = userStatusKey === CONTENT_STATUS.FINISHED;

      isTrackable = item.is_trackable;

      statusText =
        item.has_flags === false && isCompleted
          ? userStatusLabelMap.finished
          : getLabelStatusText(userStatusKey, isTrackable);

      if (isTrackable) {
        if (isCompleted) {
          infoTooltipText = 'All flags correctly captured';
          progress = 100;
        } else {
          const totalFlags = item.flags?.length ?? 0;

          if (isStarted && totalFlags > 0) {
            progress = Number(((100 / totalFlags) * item.correct_flags).toFixed(2));
            infoTooltipText = `${item.correct_flags}/${totalFlags} flags captured`;
          } else if (isStarted) {
            progress = 0;
            infoTooltipText = getTooltipText(item.content_type, userStatusKey);
          } else {
            progress = 0;
            infoTooltipText = getTooltipText(item.content_type, userStatusKey);
          }
        }
      } else if (item.has_flags === false) {
        infoTooltipText = FLAGLESS_LAB_TOOLTIP;
      } else {
        infoTooltipText = getTooltipText(item.content_type, CONTENT_STATUS.NON_TRACKABLE);
      }
      break;
    }
    case LAB_TYPES.SKILL_CHECK: {
      userStatusKey = item.user_status as string;
      isUnstarted = false;
      isStarted = userStatusKey === CONTENT_STATUS.STARTED;
      isCompleted = userStatusKey === CONTENT_STATUS.FINISHED;

      isTrackable = true;
      progress = item.flags?.length
        ? Math.round((item.correct_flags / item.flags.length) * 100)
        : 0;

      statusText = getLabelStatusText(userStatusKey, isTrackable, progress);

      if (isCompleted) {
        infoTooltipText = 'Skill Check complete';
      } else {
        infoTooltipText = getTooltipText(item.content_type, userStatusKey, progress);
      }
      break;
    }

    case CONTENT_TYPES.LEARNING_PATH:
    case CONTENT_TYPES.COURSE: {
      progress = typeof item.user_status === 'object' ? item.user_status.progress * 100 : 0;

      isUnstarted = progress === 0 && item.is_started !== true;
      isStarted = progress > 0 || item.is_started === true;
      isCompleted = progress === 100;
      isTrackable = item.has_trackable_content;

      if (isUnstarted) {
        userStatusKey = CONTENT_STATUS.UNSTARTED;
      } else if (isCompleted) {
        userStatusKey = CONTENT_STATUS.FINISHED;
      } else {
        userStatusKey = CONTENT_STATUS.STARTED;
      }

      statusText = getLabelStatusText(userStatusKey, isTrackable);

      if (isTrackable) {
        if (isCompleted) {
          infoTooltipText = 'All Course content complete';
        } else {
          infoTooltipText = getTooltipText(item.content_type, userStatusKey);
        }
      } else infoTooltipText = getTooltipText(item.content_type, CONTENT_STATUS.NON_TRACKABLE);

      break;
    }
    case CONTENT_TYPES.COLLECTION: {
      const { complete, total, percentage } = getCollectionLabProgress(item.labs);

      isTrackable = total > 0;
      // Completion is sticky server-side and never revoked, so a collection that
      // grew after the learner finished it must not read as incomplete again.
      isCompleted = item.is_finished_recorded === true || (total > 0 && complete === total);
      isStarted = !isCompleted && (complete > 0 || item.is_started === true);
      isUnstarted = !isCompleted && !isStarted;
      progress = isCompleted ? 100 : percentage;

      userStatusKey = isCompleted
        ? CONTENT_STATUS.FINISHED
        : isStarted
        ? CONTENT_STATUS.STARTED
        : CONTENT_STATUS.UNSTARTED;

      const ratio = `${complete}/${pluralize(total, 'lab')}`;
      statusText = total
        ? isCompleted && complete < total
          ? `${userStatusLabelMap.finished} · ${ratio}`
          : `${ratio} · ${percentage}%`
        : pluralize(0, 'lab');
      infoTooltipText = total
        ? `${complete} of ${pluralize(total, 'lab')} complete`
        : 'This collection has no labs and does not count towards playlist completion';

      break;
    }
    case CONTENT_TYPES.LINK:
      isCompleted = item.user_status === CONTENT_STATUS.VISITED;
      isUnstarted = item.user_status === CONTENT_STATUS.UNVISITED;
      isTrackable = item.is_trackable;

      if (isCompleted) {
        statusText = 'Opened';
        infoTooltipText = 'URL has been opened';
      } else {
        statusText = 'Not opened';
        infoTooltipText = 'URL has not been opened';
      }

      break;
    case CONTENT_TYPES.QUIZ:
      if (item.user_status === CONTENT_STATUS.PENDING) {
        userStatusKey = CONTENT_STATUS.UNSTARTED;
      } else userStatusKey = item.user_status as string;
      isTrackable = item.is_trackable;

      statusText = getLabelStatusText(userStatusKey, isTrackable);
      infoTooltipText = getTooltipText(
        item.content_type,
        isTrackable ? userStatusKey : CONTENT_STATUS.NON_TRACKABLE
      );

      break;
    case CONTENT_TYPES.EXERCISE:
      if (item.user_status === CONTENT_STATUS.UNSOLVED) {
        userStatusKey = CONTENT_STATUS.UNSTARTED;
      } else userStatusKey = item.user_status as string;
      isTrackable = item.is_trackable;

      statusText = getLabelStatusText(userStatusKey, isTrackable);
      infoTooltipText = getTooltipText(
        item.content_type,
        isTrackable ? userStatusKey : CONTENT_STATUS.NON_TRACKABLE
      );

      break;
  }

  const BarChildComponents = () => {
    switch (item.content_type) {
      case CONTENT_TYPES.VIDEO:
      case CONTENT_TYPES.LEARNING_PATH:
      case CONTENT_TYPES.COURSE:
      case CONTENT_TYPES.IFRAME:
      case CONTENT_TYPES.LAB:
      case CONTENT_TYPES.COLLECTION:
      case LAB_TYPES.SKILL_CHECK: {
        return (
          <>
            <Tooltip content={`${progress}%`} triggerRef={progressBarRef}>
              <Step active position={progress} />
            </Tooltip>
            <Bar progress={progress} />
          </>
        );
      }
      case CONTENT_TYPES.LINK: {
        return (
          <>
            <Step active={isCompleted} position={0} />
            <Step active={isCompleted} position={100} />
            <Bar progress={isCompleted ? 100 : 0} />
          </>
        );
      }
      default: {
        return null;
      }
    }
  };
  return (
    <ContentProgressBarContainer>
      {isTrackable && (
        <BarContainer ref={progressBarRef}>{!isUnstarted && <BarChildComponents />}</BarContainer>
      )}
      <StatusLabelContainer>
        <span>{statusText}</span>
        <Tooltip content={infoTooltipText} placement="topRight">
          <div>
            <Icon
              fill={isTrackable ? colors.iconButtons.purple.dark : '#F8B369'}
              icon="Info"
              size="small"
            />
          </div>
        </Tooltip>
      </StatusLabelContainer>
    </ContentProgressBarContainer>
  );
};

export default ContentProgressBar;
